/**
 * P2.2: Estado offline (red + sincronización).
 * Expone isOnline, isSyncing; al recuperar red procesa la cola y invalida queries.
 */
import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToNetworkStatus } from '../offline/networkStatus';
import { processQueue } from '../offline/syncWorker';
import { getPendingMutations } from '../offline/queue';

interface OfflineContextType {
    isOnline: boolean;
    isSyncing: boolean;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export function OfflineProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const [isOnline, setIsOnline] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const wasOffline = useRef(false);

    useEffect(() => {
        // Initial drain: if online on app launch, check if prior sessions left mutations
        if (typeof navigator !== 'undefined' && navigator.onLine) {
            getPendingMutations().then((pending) => {
                if (pending.length > 0) {
                    setIsSyncing(true);
                    processQueue(queryClient)
                        .then(({ processed, failed }) => {
                            if (processed > 0 || failed > 0) {
                                console.log('[Offline] Initial startup sync finished:', { processed, failed });
                            }
                        })
                        .catch((err) => console.error('[Offline] Initial sync error:', err))
                        .finally(() => setIsSyncing(false));
                }
            }).catch(() => {});
        }

        return subscribeToNetworkStatus((online) => {
            setIsOnline(online);
            if (online && wasOffline.current) {
                wasOffline.current = false;
                setIsSyncing(true);
                processQueue(queryClient)
                    .then(({ processed, failed }) => {
                        if (processed > 0 || failed > 0) {
                            console.log('[Offline] Sync done:', { processed, failed });
                        }
                    })
                    .catch((err) => console.error('[Offline] Sync error:', err))
                    .finally(() => setIsSyncing(false));
            }
            if (!online) wasOffline.current = true;
        });
    }, [queryClient]);

    return (
        <OfflineContext.Provider value={{ isOnline, isSyncing }}>
            {children}
        </OfflineContext.Provider>
    );
}

export function useOffline(): OfflineContextType {
    const ctx = useContext(OfflineContext);
    if (ctx === undefined) {
        throw new Error('useOffline must be used within an OfflineProvider');
    }
    return ctx;
}
